/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 *
 * Square shell — Tienda online: estado, link, perfil y horarios.
 * Scroll unificado (mobile = desk) con PaperSheet blocks.
 */
import React, { useMemo, useState } from 'react';
import { ModalAwareScrollView } from '../../shared/modals/ModalAwareScrollView';
import {
  ActivityIndicator,
  RefreshControl,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import { type KeyboardAwareScrollViewRef } from 'react-native-keyboard-controller';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useNavigation } from '@react-navigation/native';

import { ShellContentFrame } from '../../shared/layout/ShellContentFrame'
import { spacing } from '../../shared/theme/spacing'
import { ticketColors, ticketFonts } from '../../shared/theme/ticketColors';
import { PaperSheet } from '../../shared/ticket/PaperSheet'
import { TicketModuleHeader } from '../../shared/ticket/TicketModuleHeader'
import { ticketSurfaces } from '../../shared/ticket/ticketSurfaces.styles';
import { ModuleEmptyState } from '../../shared/ui/ModuleEmptyState';
import { useScreenLoadShell } from '../../shared/ui/hooks-screen/useScreenLoad';
import { OperationalConfirmModal } from '../../shared/modals/OperationalConfirmModal';
import { hasCapability } from '../../shell/feature-flags/businessCapabilitiesService';
import { useBusinessProfileStore } from '../../shell/session/store/useBusinessProfileStore';

import { useBranchSettingsScreen } from './useBranchSettingsScreen';
import { resolveStoreDoorState } from '../domain/storeProfileTypes';
import { PublicStoreProfileSection } from './components/PublicStoreProfileSection';
import { ScheduledClosuresSection } from './components/ScheduledClosuresSection';
import { TermsModal } from './components/TermsModal';
import { customerPortalBaseUrl } from '../../shared/customerPortalUrl';
import { branchMissingMapLocation } from '../domain/branchLocationStatus';

type ConfirmState =
  | { kind: 'force_close'; durationMinutes?: number }
  | { kind: 'force_open' }
  | { kind: 'auto' }
  | { kind: 'deactivate_public' }
  | null;

const PORTAL_BASE = customerPortalBaseUrl();

export function StorefrontHubScreen() {
  const insets = useSafeAreaInsets();
  const navigation = useNavigation<any>();

  const vm = useBranchSettingsScreen();
  const businessProfile = useBusinessProfileStore((s) => s.profile);
  const onDutyAllowed = hasCapability(businessProfile, 'on_duty');

  const [confirm, setConfirm] = useState<ConfirmState>(null);
  const [confirmBusy, setConfirmBusy] = useState(false);

  const { refreshing, onRefresh } = useScreenLoadShell('StorefrontHub', async (opts) => {
    await vm.load(opts);
  });

  // —— Confirm modal copy — debe ir antes de los early returns (VUELTO-NEGOCIO-20) ——

  const confirmCopy = useMemo(() => {
    if (!confirm) return null;
    if (confirm.kind === 'deactivate_public') {
      return {
        title: '¿Desactivar tienda online?',
        message:
          'Si la apagás, quien tenga el link no entra ni puede pedir.\n\nHorarios, logo y datos se quedan. La podés prender de nuevo.',
        confirmLabel: 'Desactivar',
        destructive: true,
        iconName: 'storefront-outline' as const,
      };
    }
    if (confirm.kind === 'auto') {
      return {
        title: '¿Volver al horario?',
        message: 'Se cancela el ajuste manual. La tienda vuelve al horario.',
        confirmLabel: 'Volver al horario',
        destructive: false,
        iconName: 'time-outline' as const,
      };
    }
    if (confirm.kind === 'force_open') {
      return {
        title: '¿Abrir la tienda ahora?',
        message: 'Se abre aunque esté fuera de horario. Vuelve al horario cuando quieras.',
        confirmLabel: 'Abrir ahora',
        destructive: false,
        iconName: 'lock-open-outline' as const,
      };
    }
    if (confirm.durationMinutes === 60) {
      return {
        title: '¿Cerrar por 1 hora?',
        message: 'Se reabre sola al terminar, si toca atender.',
        confirmLabel: 'Cerrar 1 hora',
        destructive: true,
        iconName: 'pause-circle-outline' as const,
      };
    }
    if (confirm.durationMinutes === 180) {
      return {
        title: '¿Cerrar por 3 horas?',
        message: 'Se reabre sola al terminar, si toca atender.',
        confirmLabel: 'Cerrar 3 horas',
        destructive: true,
        iconName: 'pause-circle-outline' as const,
      };
    }
    return {
      title: '¿Cerrar la tienda ahora?',
      message: 'Queda cerrada hasta que vuelvas al horario.',
      confirmLabel: 'Cerrar ahora',
      destructive: true,
      iconName: 'pause-circle-outline' as const,
    };
  }, [confirm]);

  // —— Loading / permission guards —inline (no BranchSettingsHubShell) ——

  if (!vm.currentBranchId) {
    return (
      <ShellContentFrame style={ticketSurfaces.screen}>
        <View style={styles.guardPad}>
          <ModuleEmptyState
            title="Seleccioná una sucursal"
            subtitle="Necesitás una sucursal activa para ver estos ajustes."
            primaryAction={{ label: 'Ir al selector', onPress: vm.handleOpenBranchSelector }}
          />
        </View>
      </ShellContentFrame>
    );
  }

  if (vm.loading) {
    return (
      <ShellContentFrame style={ticketSurfaces.screen}>
        <View style={styles.loadingWrap}>
          <ActivityIndicator color={ticketColors.gold} />
          <Text style={styles.loadingText}>Cargando…</Text>
        </View>
      </ShellContentFrame>
    );
  }

  if (!vm.canView) {
    return (
      <ShellContentFrame style={ticketSurfaces.screen}>
        <View style={styles.guardPad}>
          <ModuleEmptyState
            title="Sin permiso"
            subtitle="Pedile al dueño que te habilite «Ajustes de sucursal» en permisos por empleado."
            primaryAction={{ label: 'Volver', onPress: () => (navigation.canGoBack() ? navigation.goBack() : navigation.navigate('Profile' as never)) }}
          />
        </View>
      </ShellContentFrame>
    );
  }

  if (!vm.publicStoreFeaturesEnabled) {
    return (
      <ShellContentFrame style={ticketSurfaces.screen}>
        <View style={styles.guardPad}>
          <ModuleEmptyState
            title="Tienda online no disponible"
            subtitle="Esta función no está habilitada para tu rubro o plan."
          />
        </View>
      </ShellContentFrame>
    );
  }

  const draft = vm.storeProfileDraft;
  const doorState = resolveStoreDoorState(draft);
  const paused = doorState === 'paused';
  const statusLabel = !draft.isPublic
    ? 'Apagada'
    : paused
      ? 'Pausada'
      : doorState === 'closed'
        ? 'Cerrada'
        : 'Abierta';

  const handleConfirm = async () => {
    if (!confirm) return;
    setConfirmBusy(true);
    try {
      if (confirm.kind === 'deactivate_public') {
        await vm.handleDeactivatePublicStore();
      } else if (confirm.kind === 'auto') {
        await vm.handleManualOverrideChange('auto');
      } else if (confirm.kind === 'force_open') {
        await vm.handleManualOverrideChange('force_open');
      } else {
        await vm.handleManualOverrideChange('force_close', confirm.durationMinutes);
      }
      setConfirm(null);
    } finally {
      setConfirmBusy(false);
    }
  };

  const canEditAny = vm.canEditProfile || vm.canEditHours || vm.canPublish;
  const branchName = vm.currentBranch?.name ?? '';

  return (
    <ShellContentFrame style={ticketSurfaces.screen}>
      <ModalAwareScrollView
        style={styles.scroll}
        contentContainerStyle={[
          styles.content,
          { paddingBottom: insets.bottom + (canEditAny ? 80 : 24) },
        ]}
        keyboardShouldPersistTaps="handled"
        bottomOffset={spacing.sm}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={onRefresh} tintColor={ticketColors.gold} colors={[ticketColors.gold]} />
        }
      >
        <View style={styles.stack}>
          {/* Header */}
          <TicketModuleHeader
            kicker="Canal online"
            title="Tienda online"
            meta={`${branchName} · ${statusLabel}`}
            trailing={
              canEditAny && vm.storeProfileDirty ? (
                <TouchableOpacity
                  style={styles.saveBtnTrailing}
                  onPress={() => void vm.handleSaveStoreProfile()}
                  disabled={vm.storeProfileSaving || vm.logoUploading || vm.coverUploading || vm.backgroundUploading}
                  accessibilityRole="button"
                  accessibilityLabel="Guardar"
                >
                  <Text style={styles.saveBtnTrailingText}>
                    {vm.storeProfileSaving ? 'Guardando…' : 'Guardar'}
                  </Text>
                </TouchableOpacity>
              ) : null
            }
          />

          {(() => {
            const missingLocation = branchMissingMapLocation(vm.currentBranch ?? {});
            const items = [
              { ok: draft.isPublic, label: draft.isPublic ? 'Tienda prendida' : 'Prendé la tienda' },
              { ok: !missingLocation, label: missingLocation ? 'Falta la ubicación del local' : 'Ubicación cargada' },
              {
                ok: draft.isPublic,
                label: draft.isPublic
                  ? 'Los productos con En tienda son los que ve el cliente'
                  : 'Publicá al menos un producto y marcá En tienda',
              },
            ];
            if (draft.isPublic && !missingLocation) return null;
            return (
              <PaperSheet padded>
                <Text style={{ fontFamily: ticketFonts.sans, fontSize: 14, color: ticketColors.ink, fontWeight: '700' }}>
                  Para que te pidan
                </Text>
                {items.map((item) => (
                  <Text
                    key={item.label}
                    style={{
                      fontFamily: ticketFonts.sans,
                      fontSize: 13,
                      color: item.ok ? ticketColors.ok : ticketColors.ink2,
                      marginTop: 6,
                      lineHeight: 18,
                    }}
                  >
                    {item.ok ? 'Listo · ' : 'Falta · '}
                    {item.label}
                  </Text>
                ))}
              </PaperSheet>
            );
          })()}

          {/* Body blocks */}
          {vm.storeProfileLoading ? (
            <PaperSheet padded>
              <View style={styles.bodyLoading}>
                <ActivityIndicator color={ticketColors.gold} />
                <Text style={styles.loadingText}>Cargando perfil público…</Text>
              </View>
            </PaperSheet>
          ) : (
            <PublicStoreProfileSection
              draft={draft}
              saving={vm.storeProfileSaving}
              dirty={vm.storeProfileDirty}
              logoUploading={vm.logoUploading}
              coverUploading={vm.coverUploading}
              backgroundUploading={vm.backgroundUploading}
              canEdit={canEditAny}
              canEditProfile={vm.canEditProfile}
              canEditHours={vm.canEditHours}
              canPublish={vm.canPublish}
              businessTypeCode={vm.businessTypeCode}
              kitchenTicketsEnabled={hasCapability(businessProfile, 'kitchen_tickets')}
              onDutyAllowed={onDutyAllowed}
              branchName={branchName}
              portalBaseUrl={PORTAL_BASE}
              onChange={vm.patchStoreProfileDraft}
              onBusinessHourChange={vm.handleBusinessHourChange}
              onAddShift={vm.handleAddShift}
              onRemoveShift={vm.handleRemoveShift}
              onShiftChange={vm.handleShiftChange}
              onRequestForceClose={(mins) => setConfirm({ kind: 'force_close', durationMinutes: mins })}
              onRequestForceOpen={() => setConfirm({ kind: 'force_open' })}
              onRequestReturnToSchedule={() => setConfirm({ kind: 'auto' })}
              onRequestDeactivatePublic={() => setConfirm({ kind: 'deactivate_public' })}
              tcAcceptedAt={draft.tcAcceptedAt}
              onTcRequired={() => vm.setTcModalVisible(true)}
              onUploadLogo={() => void vm.handleUploadLogo()}
              onRemoveLogo={() => void vm.handleRemoveLogo()}
              onUploadCover={() => void vm.handleUploadCover()}
              onRemoveCover={() => void vm.handleRemoveCover()}
              onUploadBackground={() => void vm.handleUploadBackground()}
              onRemoveBackground={() => void vm.handleRemoveBackground()}
              onSave={() => void vm.handleSaveStoreProfile()}
              onCheckSlug={vm.checkSlugAvailable}
            />
          )}

          {/* Cierres programados */}
          <ScheduledClosuresSection
            closures={vm.closures}
            loading={vm.closuresLoading}
            canEdit={vm.canEditHours}
            onCreate={(start, end, reason) => void vm.handleCreateClosure(start, end, reason)}
            onDelete={(id) => void vm.handleDeleteClosure(id)}
          />
        </View>
      </ModalAwareScrollView>

      {/* Save bar — sticky bottom */}
      {canEditAny && vm.storeProfileDirty ? (
        <View style={[styles.saveBar, { paddingBottom: insets.bottom + 8 }]}>
          <Text style={styles.dirtyHint}>Cambios sin guardar</Text>
          <TouchableOpacity
            style={[
              styles.saveBtn,
              (vm.storeProfileSaving || vm.logoUploading || vm.coverUploading || vm.backgroundUploading) && styles.saveBtnDisabled,
            ]}
            onPress={() => void vm.handleSaveStoreProfile()}
            disabled={vm.storeProfileSaving || vm.logoUploading || vm.coverUploading || vm.backgroundUploading}
            accessibilityRole="button"
            accessibilityLabel="Guardar cambios"
          >
            <Text style={styles.saveBtnText}>
              {vm.storeProfileSaving ? 'Guardando…' : 'Guardar'}
            </Text>
          </TouchableOpacity>
        </View>
      ) : null}

      {/* Overlays fuera del scroll */}
      <TermsModal
        visible={vm.tcModalVisible}
        loading={vm.tcAccepting}
        onAccept={() => void vm.handleAcceptTc()}
        onCancel={() => {
          if (!vm.tcAccepting) vm.setTcModalVisible(false);
        }}
      />
      {confirmCopy ? (
        <OperationalConfirmModal
          visible={confirm != null}
          title={confirmCopy.title}
          message={confirmCopy.message}
          confirmLabel={confirmCopy.confirmLabel}
          destructive={confirmCopy.destructive}
          iconName={confirmCopy.iconName}
          loading={confirmBusy}
          onClose={() => {
            if (!confirmBusy) setConfirm(null);
          }}
          onConfirm={() => void handleConfirm()}
        />
      ) : null}
    </ShellContentFrame>
  );
}

const styles = StyleSheet.create({
  scroll: {
    flex: 1,
  },
  content: {
    paddingHorizontal: spacing.md,
    paddingTop: spacing.sm,
    flexGrow: 1,
  },
  stack: {
    gap: 12,
    maxWidth: 920,
    width: '100%',
    alignSelf: 'center',
  },
  guardPad: {
    flex: 1,
    justifyContent: 'center',
    paddingHorizontal: spacing.lg,
  },
  loadingWrap: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    gap: spacing.sm,
  },
  bodyLoading: {
    alignItems: 'center',
    gap: spacing.sm,
    paddingVertical: spacing.lg,
  },
  loadingText: {
    fontSize: 14,
    color: ticketColors.ink2,
    fontFamily: ticketFonts.sans,
  },
  saveBtnTrailing: {
    backgroundColor: ticketColors.gold,
    borderRadius: 10,
    paddingHorizontal: 16,
    paddingVertical: 10,
    minHeight: 42,
    justifyContent: 'center',
  },
  saveBtnTrailingText: {
    fontSize: 14,
    fontWeight: '700',
    color: ticketColors.goldInk,
    fontFamily: ticketFonts.sans,
  },
  saveBar: {
    position: 'absolute' as const,
    bottom: 0,
    left: 0,
    right: 0,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: spacing.md,
    paddingTop: spacing.sm,
    backgroundColor: ticketColors.paper,
    borderTopWidth: 1,
    borderTopColor: ticketColors.ruleSoft,
  },
  dirtyHint: {
    fontSize: 13,
    color: ticketColors.ink2,
    fontFamily: ticketFonts.sans,
  },
  saveBtn: {
    backgroundColor: ticketColors.gold,
    borderRadius: 10,
    paddingHorizontal: 20,
    paddingVertical: 10,
    minHeight: 42,
    justifyContent: 'center',
  },
  saveBtnDisabled: {
    opacity: 0.5,
  },
  saveBtnText: {
    fontSize: 14,
    fontWeight: '700',
    color: ticketColors.goldInk,
    fontFamily: ticketFonts.sans,
  },
});
